/**
 * LogRingBuffer — buffer circular in-memory que captura as últimas N entradas
 * de console (log/info/warn/error/debug) pra retrieval sob demanda via
 * comando remoto {@code logs:tail} da Visualização Interativa.
 *
 * <p>Diferente do {@link RemoteLogger} (batched flush p/ /api/v1/logs,
 * feature-flagged): este buffer é <b>sempre-on</b>, baixo overhead, e não
 * faz tráfego de rede. O comando {@code logs:tail} lê snapshot do buffer
 * no momento da requisição.</p>
 *
 * <p><b>Chain de interceptors:</b> {@code init()} preserva o {@code console.log}
 * atual (que pode já ter sido interceptado pelo RemoteLogger ou outro tooling)
 * e chama em seguida — funciona empilhando-se sobre o que já estiver no
 * console, sem quebrar comportamento existente.</p>
 *
 * <p><b>Cenário:</b> tablet RD relata "vídeo travando às 14h35". Operador
 * dispara {@code logs:tail {lines: 200}} → recebe últimos logs sem precisar
 * deslocar técnico nem ativar feature flag de remote logging.</p>
 */

export type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

export interface LogEntry {
  /** ISO timestamp da captura. */
  timestamp: string;
  level: LogLevel;
  /** Mensagem formatada (args serializados e concatenados com space). */
  message: string;
}

const DEFAULT_CAPACITY = 500;

class LogRingBuffer {
  private capacity = DEFAULT_CAPACITY;
  private buffer: LogEntry[] = [];
  private head = 0; // próxima posição de escrita
  private size_ = 0; // qtd de entries válidas (<= capacity)
  private initialized = false;
  private originalConsole: Partial<
    Record<LogLevel, (...args: unknown[]) => void>
  > = {};

  /**
   * Começa a interceptar console.{log,info,warn,error,debug}. Idempotente —
   * múltiplas chamadas são no-op. Capacity opcional (default 500).
   */
  init(capacity: number = DEFAULT_CAPACITY): void {
    if (this.initialized) return;
    this.capacity = capacity;
    this.buffer = new Array(capacity);
    this.initialized = true;

    const levels: LogLevel[] = ['log', 'info', 'warn', 'error', 'debug'];
    for (const level of levels) {
      const prev = console[level].bind(console);
      this.originalConsole[level] = prev;
      console[level] = (...args: unknown[]) => {
        try {
          this.push(level, args);
        } catch {
          // Captura nunca pode quebrar o console — silencia erros internos.
        }
        prev(...args);
      };
    }
  }

  /**
   * Retorna as últimas {@code lines} entradas em ordem cronológica (mais
   * antiga → mais recente). {@code lines} é clamped a [1, capacity].
   */
  tail(lines: number = 100): LogEntry[] {
    const wanted = Math.max(1, Math.min(lines, this.capacity));
    const available = Math.min(wanted, this.size_);
    const result: LogEntry[] = new Array(available);
    // Começa em (head - available), wrap-around.
    const start = (this.head - available + this.capacity) % this.capacity;
    for (let i = 0; i < available; i++) {
      result[i] = this.buffer[(start + i) % this.capacity];
    }
    return result;
  }

  /** Quantidade de entries atualmente no buffer (≤ capacity). */
  size(): number {
    return this.size_;
  }

  /** Capacidade configurada do buffer. */
  getCapacity(): number {
    return this.capacity;
  }

  /** Limpa o buffer (mantém interceptação ativa). Útil pra debug/teste. */
  clear(): void {
    this.buffer = new Array(this.capacity);
    this.head = 0;
    this.size_ = 0;
  }

  /**
   * Adiciona uma entrada direto no buffer SEM passar pelo {@code console.*}.
   *
   * <p>Usado pelo {@link logger} pra capturar mensagens cujo nível ficou
   * abaixo do gate de {@code VITE_LOG_LEVEL}: a mensagem é silenciada no
   * DevTools (não polui), mas o buffer continua íntegro pra retrieval via
   * comando remoto {@code logs:tail}. Isso preserva o trade-off "console
   * limpo + audit trail completo".</p>
   *
   * <p>No-op se o buffer ainda não foi {@link #init initialize}.</p>
   */
  record(level: LogLevel, args: unknown[]): void {
    if (!this.initialized) return;
    this.push(level, args);
  }

  /**
   * Restaura o console original e desliga a captura. Idempotente. Usar
   * somente em teardown de testes — em produção o buffer fica ativo pra
   * todo o ciclo de vida do app.
   */
  stop(): void {
    if (!this.initialized) return;
    for (const level of Object.keys(this.originalConsole) as LogLevel[]) {
      const orig = this.originalConsole[level];
      if (orig) console[level] = orig;
    }
    this.originalConsole = {};
    this.initialized = false;
  }

  private push(level: LogLevel, args: unknown[]): void {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message: this.formatArgs(args),
    };
    this.buffer[this.head] = entry;
    this.head = (this.head + 1) % this.capacity;
    if (this.size_ < this.capacity) this.size_++;
  }

  private formatArgs(args: unknown[]): string {
    return args
      .map((arg) => {
        if (typeof arg === 'string') return arg;
        if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
        try {
          return JSON.stringify(arg);
        } catch {
          return String(arg);
        }
      })
      .join(' ');
  }
}

// Singleton — uma única instância pro app inteiro.
export const logRingBuffer = new LogRingBuffer();
export default logRingBuffer;

// Class export pra testes (instanciação isolada).
export { LogRingBuffer };
